import {identity} from '@packages/utilities/fp/utils';
import {type Json} from '@packages/utilities/json';
import {Result} from '@packages/systems/wfdl';
import {type Timeline} from '@packages/utilities/timeline';
import {type Effect, Impure} from './EffectType';
import {type Value, run} from './Run';
import {
  type InviteUserResponseType as InviteUserByEmailResponse,
  type UserRecordResponseType as UserRecordResponse,
  EMAIL,
  USER_ID,
} from '@packages/systems/flows/types';

export type Method =
  | {
      tag: 'GET';
      val: Record<any, any>;
    }
  | {
      tag: 'POST';
      val: string;
    }
  | {
      tag: 'PUT';
      val: string;
    }
  | {
      tag: 'PATCH';
      val: string;
    }
  | {
      tag: 'DELETE';
      val: Record<any, any>;
    };

export type NameValueEntry = {
  name: string;
  value: string;
};
export type NameValueMap = Array<NameValueEntry>;

export type HttpRequestVal = {
  url: string;
  method: Method;
  headers: NameValueMap;
};

export type HttpRequestResponse = {
  ['Response status code']: number;
  ['Response status message']: string;
  ['Response body']: string;
};

/** Email */
export type SendEmailVal = {
  To: Array<string>;
  Subject: string;
  Message: string;
};

export type SendEmailResponse = Record<any, any>;

/** CMS */
type ItemStatus =
  | {
      tag: 'draft';
      val: Record<any, any>;
    }
  | {
      tag: 'publish';
      val: Record<any, any>;
    }
  | {
      tag: 'staged';
      val: Record<any, any>;
    };

type UpdateItemStatus =
  | {
      tag: 'draft';
      val: Record<any, any>;
    }
  | {
      tag: 'publish';
      val: Record<any, any>;
    }
  | {
      tag: 'staged';
      val: Record<any, any>;
    }
  | {
      tag: 'archived';
      val: Record<any, any>;
    }
  | {
      tag: 'current';
      val: Record<any, any>;
    };

export type CmsCreateItemVal = {
  collectionName: string;
  itemName: string;
  itemSlug: string;
  fields: Array<{
    name: string;
    value: Json;
  }>;
  status: string;
};

export type CmsCreateItemResponse = {
  ['Custom Fields']: Array<{
    name: string;
    value: Json;
  }>;
  ['Item ID']: string;
  Name: string;
  Slug: string;
  ['Item URL']: string;
  ['Item Status']: ItemStatus;
  ['Created On']: string;
  ['Created By']: string;
};

export type CmsLookupItemResponse = CmsCreateItemResponse;

export type CmsUpdateItemResponse = {
  ['Custom Fields']: Array<{
    name: string;
    value: Json;
  }>;
  ['Item ID']: string;
  Name: string;
  Slug: string;
  ['Item URL']: string;
  ['Item Status']: UpdateItemStatus;
  ['Created On']: string;
  ['Created By']: string;
};

export type CmsDeleteItemVal = {
  itemId: string;
  collectionName: string;
};

export type CmsDeleteItemResponse = Partial<Record<any, any>>;

export type CmsLookupItemVal = {
  collectionName: string;
  searchBy: {
    name: string;
    value: Json;
  };
};
export type CmsUpdateItemVal = {
  collectionName: string;
  itemId: string;
  status: string;
  fields: Array<{
    slug: string;
    value: Json;
  }>;
};

/** Users */
export type InviteUserByEmailVal = {
  Email: string;
  AccessGroups: Array<string>;
};

export type UserIdentifierVal = {
  userIdentifier: string;
  userIdentifierType: typeof USER_ID | typeof EMAIL;
};

export type UpdateUserVal = {
  user: UserIdentifierVal;
  fieldsForUpdate: {
    acceptCommunications?: boolean;
    accessGroups?: Array<string>;
  };
};

/**
 * The structure that io actions evaluate to
 */
export type IO =
  | {
      tag: 'SendEmail';
      val: SendEmailVal;
    }
  | {
      tag: 'HttpRequest';
      val: HttpRequestVal;
    }
  | {
      tag: 'CmsCreateItem';
      val: CmsCreateItemVal;
    }
  | {
      tag: 'CmsDeleteItem';
      val: CmsDeleteItemVal;
    }
  | {
      tag: 'CmsUpdateItem';
      val: CmsUpdateItemVal;
    }
  | {
      tag: 'CmsLookupItem';
      val: CmsLookupItemVal;
    }
  | {
      tag: 'ConsoleLog';
      val: string;
    }
  | {
      tag: 'InviteUserByEmail';
      val: InviteUserByEmailVal;
    }
  | {
      tag: 'DeleteUser';
      val: UserIdentifierVal;
    }
  | {
      tag: 'UpdateUser';
      val: UpdateUserVal;
    };

export const sendEmail = <err>(
  val: SendEmailVal
): Effect<IO, err, Record<any, any>> => Impure({tag: 'SendEmail', val});

export const httpRequest = <err>(
  val: HttpRequestVal
): Effect<IO, err, string> => Impure({tag: 'HttpRequest', val});

export const cmsCreateItem = <err>(
  val: CmsCreateItemVal
): Effect<IO, err, CmsCreateItemResponse> =>
  Impure({tag: 'CmsCreateItem', val});

export const cmsDeleteItem = <err>(
  val: CmsDeleteItemVal
): Effect<IO, err, CmsDeleteItemResponse> =>
  Impure({tag: 'CmsDeleteItem', val});

export const cmsLookupItem = <err>(
  val: CmsLookupItemVal
): Effect<IO, err, CmsCreateItemResponse> =>
  Impure({tag: 'CmsLookupItem', val});
export const cmsUpdateItem = <err>(
  val: CmsUpdateItemVal
): Effect<IO, err, CmsUpdateItemResponse> =>
  Impure({tag: 'CmsUpdateItem', val});

export const consoleLog = <err>(
  val: string
): Effect<IO, err, Record<any, any>> => Impure({tag: 'ConsoleLog', val});

export const inviteUser = <err>(
  val: InviteUserByEmailVal
): Effect<IO, err, InviteUserByEmailResponse> =>
  Impure({tag: 'InviteUserByEmail', val});

export const deleteUser = <err>(
  val: UserIdentifierVal
): Effect<IO, err, UserRecordResponse> => Impure({tag: 'DeleteUser', val});

export const updateUser = <err>(val: any): Effect<IO, err, void> =>
  Impure({tag: 'UpdateUser', val});

/**
 * Capabilities contains the actual side-effecting functions. It is expected
 * that they all resolve to Json in order to be able to serialize their results
 * for the purpose of resuming effects.
 */
export type Capabilities = {
  Http: {
    request: (arg1: HttpRequestVal) => Promise<HttpRequestResponse>;
  };
  Email: {
    sendEmail: (arg1: SendEmailVal) => Promise<SendEmailResponse>;
  };
  CMS: {
    createItem: (arg1: CmsCreateItemVal) => Promise<CmsCreateItemResponse>;
    deleteItem: (arg1: CmsDeleteItemVal) => Promise<CmsDeleteItemResponse>;
    lookupItem: (arg1: CmsLookupItemVal) => Promise<CmsLookupItemResponse>;
    updateItem: (arg1: CmsUpdateItemVal) => Promise<CmsUpdateItemResponse>;
  };
  Console: {
    log: (arg1: string) => Promise<Record<any, any>>;
  };
  User: {
    inviteUser: (
      arg1: InviteUserByEmailVal
    ) => Promise<InviteUserByEmailResponse>;
    deleteUser: (arg1: UserIdentifierVal) => Promise<UserRecordResponse>;
    updateUser: (arg1: UpdateUserVal) => Promise<UserRecordResponse>;
  };
};

// The return values of all Capabilities is valid Json but we can’t convince
// Flow without type-casting.
const returnJson: (
  arg1: (arg1: IO) => Promise<any>
) => (arg1: IO) => Promise<Json> = identity;

/**
 * Run an effect with the provided capabilities and starting timeline.
 */
export const runWithCapabilities = (
  cap: Capabilities
): (<err>(
  arg1: Timeline,
  arg2: Effect<IO, err, Value>
) => Promise<{
  timeline: Timeline;
  result: Result.ResultType<err, Value>;
}>) =>
  run<IO, any, Value>(
    returnJson((dsl) => {
      if (dsl.tag === 'SendEmail') {
        const config = dsl.val;
        return cap.Email.sendEmail({
          To: config.To,
          Subject: config.Subject,
          Message: config.Message,
        });
      }

      if (dsl.tag === 'HttpRequest') {
        return cap.Http.request(dsl.val);
      }

      if (dsl.tag === 'CmsCreateItem') {
        return cap.CMS.createItem(dsl.val);
      }

      if (dsl.tag === 'CmsDeleteItem') {
        return cap.CMS.deleteItem(dsl.val);
      }

      if (dsl.tag === 'CmsLookupItem') {
        return cap.CMS.lookupItem(dsl.val);
      }
      if (dsl.tag === 'CmsUpdateItem') {
        return cap.CMS.updateItem(dsl.val);
      }

      if (dsl.tag === 'InviteUserByEmail') {
        return cap.User.inviteUser(dsl.val);
      }

      if (dsl.tag === 'DeleteUser') {
        return cap.User.deleteUser(dsl.val);
      }

      if (dsl.tag === 'UpdateUser') {
        return cap.User.updateUser(dsl.val);
      }

      // dsl.tag === 'ConsoleLog'
      return cap.Console.log(dsl.val);
    })
  );
